import { Injectable } from '@angular/core';
import { gql } from 'apollo-angular';
import { Observable, Subscription, catchError, map, of } from 'rxjs';
import { BudgetSupportTypesQueryService } from 'src/app/shared/graphql/jobs/get-default-service-category-query.graphql.autogenerated';
import { FlexJobActionRequest } from 'src/app/shared/models/flex-job-action-request.model';
import { JobCreateUpdateRequest } from 'src/app/shared/models/gql/job-create-update-request.model';
import { PrefillLastMinuteJobAttributes } from 'src/app/shared/models/gql/pre-fill-last-minute-job-attributes.model';
import { BudgetSupportType } from 'src/app/shared/models/gql/rate.model';
import { ActionCableService } from 'src/app/shared/services/action-cable.service';
import { ApiInterfaceService } from 'src/app/shared/services/api-interface.service';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { Broadcaster } from 'src/app/shared/services/ngx-cable.service';

/**
 * Provides CRUD interface for flex jobs. To get the current jobs
 * in a component, call list() and refresh() when necessary.
 */
@Injectable({
  providedIn: 'root',
})
export class FlexJobsService {
  constructor(
    public jobsService: JobsService,
    private actionCableService: ActionCableService,
    private broadcaster: Broadcaster,
    private api: ApiInterfaceService,
    private budgetSupportTypesQueryService: BudgetSupportTypesQueryService,
  ) {}

  // Channel subscriber for Job bids
  private jobBidsChannelSub: Subscription;

  /**
   * Sends a mutation to post last minute job.
   *
   * @param createLastMinuteJobInput - last minute job object
   */
  async createLastMinuteJob(createLastMinuteJobInput: JobCreateUpdateRequest): Promise<any> {
    try {
      const postedJobData = await this.api.gqlMutation(
        gql`
          mutation createLastMinuteJob($createLastMinuteJobInput: FlexJobCreateUpdateInput!) {
            flexJobCreateUpdate(input: $createLastMinuteJobInput) {
              clientMutationId
              postedJob {
                clientFields {
                  postedWithin14DaysOfUserSignUp
                  firstPostedJob
                }
                id
                status {
                  clientStatus
                }
              }
            }
          }
        `,
        { createLastMinuteJobInput },
      );
      return postedJobData['flexJobCreateUpdate']['postedJob'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a graphql mutation for accept or cancel flex job.
   *
   * @param flexJobClientActionRequest - flex job action object.
   */
  async flexJobClientAction(flexJobClientActionRequest: FlexJobActionRequest): Promise<any> {
    try {
      const flexJobsData = await this.api.gqlMutation(
        gql`
          mutation clientFlexJobs($flexJobClientActionRequest: AcceptCancelFlexJobsInput!) {
            clientFlexJobs(input: $flexJobClientActionRequest) {
              clientMutationId
            }
          }
        `,
        {
          flexJobClientActionRequest,
        },
      );
      return flexJobsData['clientFlexJobs'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a graphql mutation for offer, decline or withdraw flex job.
   *
   * @param flexJobWorkerActionRequest - flex job action object.
   */
  async flexJobWorkerAction(flexJobWorkerActionRequest: FlexJobActionRequest): Promise<any> {
    try {
      const flexJobsData = await this.api.gqlMutation(
        gql`
          mutation workerFlexJobs($flexJobWorkerActionRequest: OfferDeclineWithdrawFlexJobsInput!) {
            workerFlexJobs(input: $flexJobWorkerActionRequest) {
              clientMutationId
            }
          }
        `,
        {
          flexJobWorkerActionRequest,
        },
      );
      return flexJobsData['workerFlexJobs'];
    } catch (e) {
      return Promise.reject(e);
    }
  }

  /**
   * Sends a query to fetch information for prefilling a last minute job form using the derivedUniqueId of a cancelled
   * or declined scheduled session.
   *
   * @param scheduledEventDerivedUniqueId - The unique id of the session.
   * @returns the PrefillLastMinuteJobAttributes for pre-filling LMJ form.
   */
  async fetchLastMinuteJobInfoFromSession(
    scheduledEventDerivedUniqueId: string | number,
  ): Promise<PrefillLastMinuteJobAttributes> {
    try {
      const res = await this.api.gqlQuery(
        gql`
          query PrefillLastMinuteJobFromSession($derivedUniqueId: String!) {
            prefillLastMinuteJobFromSession(derivedUniqueId: $derivedUniqueId) {
              suburbSelector {
                idx
                txt
              }
              title
              startTime
              duration
              consumerDescription
              jobDescription
              gender
              services
            }
          }
        `,
        { derivedUniqueId: scheduledEventDerivedUniqueId },
      );
      return res['prefillLastMinuteJobFromSession'];
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Unsubscribe from jobs bids.
   *
   */
  unsubscribeFromChannel(): void {
    if (this.jobBidsChannelSub) {
      this.actionCableService.removeChannel(this.jobBidsChannelSub);
      this.jobBidsChannelSub = null;
    }
  }

  /**
   * Subscribe to jobs bids using  job id and get new bids.
   *
   * @param jobId - Job id.
   */
  async subscribeToJobBidsChannel(jobId) {
    this.jobBidsChannelSub = await this.actionCableService.subscribeToChannel('JobBidsChannel', { id: jobId });
  }

  /**
   * Broadcaster for jobs bids.
   */
  jobBidsBroadcaster() {
    return this.broadcaster.on('JobBidsChannel');
  }

  /**
   * Gets the default service category based on the postedJobId and the consumerId.
   *
   * @param clientId - client id
   * @param postedJobId - posted job id
   * @returns the default service category for the current context.
   */
  getDefaultServiceCategoryFromJob(clientId: string, postedJobId: string): Observable<BudgetSupportType[]> {
    return this.budgetSupportTypesQueryService
      .fetch({
        consumerUserId: clientId,
        postedJobId,
      })
      .pipe(
        map((result) => {
          const defaultBudgetSupportTypes = result?.data?.budgetSupportTypes?.defaultBudgetSupportTypes;
          if (!defaultBudgetSupportTypes?.length) return [];
          return [
            {
              id: defaultBudgetSupportTypes[0].id,
              name: defaultBudgetSupportTypes[0].name,
              displayName: defaultBudgetSupportTypes[0].displayName,
              isAdminOnlyType: defaultBudgetSupportTypes[0].isAdminOnlyType,
            },
          ];
        }),
        catchError((error) => {
          console.error('[FlexJobsService] Error fetching the default budget support type', error);
          return of(null);
        }),
      );
  }
}
